/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailExplain.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEEMAILEXPLAIN-001
 * PURPOSE: Fetch the provenance/explanation for one email from the
 *          org-scoped GET /api/emails/<id>/explain/ endpoint (Phase 4a).
 * WHY:     Phase 4 of the email-intelligence upgrade — make every decision
 *          explainable. RT-03 (#128) adds a `decision` block citing the
 *          EmailDecisionJournal so the Why panel never invents placement
 *          prose.
 * RELATED:
 *   - components/WhyPanel.tsx: the consumer.
 *   - backend organisation/services/email_explain.py: the payload shape.
 */

import { useCallback, useRef, useState } from 'react'
import { quotechJson } from '../../_internal/services/quotechApi'

export interface ExplainEntityLink {
  entity_type: string
  entity_id: string
  label: string
  confidence: number | null
  score: number | null
  source: string
  status: string
  reasoning: string
}

export interface ExplainRun {
  run_id: string
  status: string
  provider: string
  error_class: string
  latency_ms: number | null
  payload: Record<string, unknown>
  created_at: string | null
}

export interface ExplainClassification {
  category: string
  confidence: number | null
  summary: string
  source: string
  entities: Record<string, unknown>
  override: { category: string; locked: boolean } | null
}

/** RT-03 — persisted EmailDecisionJournal citation (or legacy flag). */
export interface ExplainDecision {
  journal_id: number | null
  action_tier: string
  reason: string
  confidence: number | null
  selected_route: Record<string, unknown>
  evidence_read: unknown[]
  evidence_missing: unknown[]
  alternatives: unknown[]
  blocked_reason: string
  legacy_evidence_unavailable: boolean
}

export interface EmailExplanation {
  email_id: string
  classification: ExplainClassification
  runs: ExplainRun[]
  entity_links: ExplainEntityLink[]
  job_link: { job_id: number; score: number | null; status: string } | null
  decision?: ExplainDecision | null
}

export function useEmailExplain() {
  const [data, setData] = useState<EmailExplanation | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // BUG-2026-06-23-EMAIL-WHY-PANEL-STALE — the panel switches emails while
  // open; without a guard a slow `/explain/` response for the previous email
  // could resolve last and paint the wrong provenance. Track the most-recent
  // requested id and only write state when our response is still the latest.
  const activeIdRef = useRef<string | null>(null)
  const load = useCallback(async (emailId: string) => {
    activeIdRef.current = emailId
    setLoading(true)
    setError(null)
    try {
      const { data: payload } = await quotechJson<EmailExplanation>(
        `/api/emails/${emailId}/explain/`,
        { method: 'GET' },
      )
      if (activeIdRef.current !== emailId) return payload ?? null
      setData(payload ?? null)
      return payload ?? null
    } catch (err) {
      if (activeIdRef.current === emailId) {
        setError(err instanceof Error ? err.message : 'Failed to load explanation')
      }
      return null
    } finally {
      if (activeIdRef.current === emailId) setLoading(false)
    }
  }, [])

  return { data, loading, error, load }
}
